import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  CurrencyDollarIcon,
  BeakerIcon,
  UserIcon,
  BuildingOfficeIcon,
  CubeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { samplesAPI, Sample, Deal, dealsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { StatusUpdateModal, FeedbackModal } from './SampleModals';
import QuoteModal from '../deals/QuoteModal';
import { Button, Badge, Spinner, Card, CardBody } from '../ui';

const SampleDetail: React.FC = () => {
  const { id, clientId } = useParams<{ id: string; clientId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sample, setSample] = useState<Sample | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clientDeals, setClientDeals] = useState<Deal[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const getSamplePath = (path: string) => isAdmin ? `/admin${path}` : path;

  useEffect(() => {
    fetchSample();
  }, [id]);

  useEffect(() => {
    if (sample?.clientId) {
      fetchClientDeals();
    }
  }, [sample?.clientId]);

  const fetchSample = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await samplesAPI.getById(Number(id));
      setSample(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sample');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDeals = async () => {
    if (!sample?.clientId) return;

    try {
      setLoadingDeals(true);
      const allDeals = await dealsAPI.getAll();
      const deals = allDeals.filter(d => d.clientId === sample.clientId);
      setClientDeals(deals);
    } catch (err: any) {
      console.error('Failed to fetch client deals:', err);
    } finally {
      setLoadingDeals(false);
    }
  };

  const handleCreateQuote = () => {
    if (!sample?.client) {
      alert('Client information is required before creating a quote.');
      return;
    }

    setShowQuoteModal(true);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this sample?')) return;
    if (!id) return;

    try {
      setDeleting(true);
      await samplesAPI.delete(Number(id));
      const backPath = clientId
        ? getSamplePath(`/clients/${clientId}`)
        : getSamplePath('/samples');
      navigate(backPath);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete sample');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusVariant = (status: string): 'warning' | 'primary' | 'success' | 'secondary' | 'error' | 'neutral' => {
    const variants: Record<string, 'warning' | 'primary' | 'success' | 'secondary' | 'error' | 'neutral'> = {
      PENDING: 'warning',
      SHIPPED: 'primary',
      DELIVERED: 'success',
      UNDER_REVIEW: 'secondary',
      APPROVED: 'success',
      REJECTED: 'error',
      RETURNED: 'neutral',
      LOST: 'error',
      EXPIRED: 'neutral',
    };
    return variants[status] || 'neutral';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      FREE_SAMPLE: 'Free Sample',
      PAID_SAMPLE: 'Paid Sample',
      TRIAL: 'Trial',
      DEMO: 'Demo',
      REPLACEMENT: 'Replacement',
      QUALITY_CHECK: 'Quality Check',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !sample) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-800">{error || 'Sample not found'}</p>
        </div>
      </div>
    );
  }

  const backPath = clientId
    ? getSamplePath(`/clients/${clientId}`)
    : getSamplePath('/samples');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(backPath)}
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to {clientId ? 'Client' : 'Samples'}
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Sample Details</h1>
            <p className="mt-1 text-sm text-neutral-500">
              {sample.trackingNumber ? `Tracking: ${sample.trackingNumber}` : 'Tracking will be added when this sample ships'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to={getSamplePath(clientId ? `/clients/${clientId}/samples/${id}/edit` : `/samples/${id}/edit`)}
            >
              <Button variant="outline" leftIcon={<PencilIcon className="h-4 w-4" />}>
                Edit
              </Button>
            </Link>
            <Button variant="primary" onClick={() => setShowStatusModal(true)}>
              Update Status
            </Button>
            {sample.status === 'APPROVED' && (
              <Button
                variant="secondary"
                onClick={handleCreateQuote}
                isLoading={loadingDeals}
                leftIcon={<DocumentDuplicateIcon className="h-4 w-4" />}
              >
                Create Quote
              </Button>
            )}
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleting}
              leftIcon={<TrashIcon className="h-4 w-4" />}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6 flex items-center gap-3">
        <Badge variant={getStatusVariant(sample.status)} size="lg">
          {sample.status.replace('_', ' ')}
        </Badge>
        <Badge variant="secondary" size="lg">
          {getTypeLabel(sample.sampleType)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Product Information */}
          <Card variant="elevated">
            <div className="px-6 py-5 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-neutral-400" />
                Client & Product Information
              </h3>
            </div>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-neutral-500">Client</p>
                  <p className="mt-1 text-sm text-neutral-900">{sample.client?.name || 'N/A'}</p>
                  {sample.client?.email && (
                    <p className="text-xs text-neutral-500">{sample.client.email}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-500">Supplier</p>
                  <p className="mt-1 text-sm text-neutral-900">{sample.supplier?.name || 'N/A'}</p>
                  {sample.supplier?.email && (
                    <p className="text-xs text-neutral-500">{sample.supplier.email}</p>
                  )}
                </div>
              </div>
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-sm font-medium text-neutral-500">Product</p>
                <p className="mt-1 text-sm text-neutral-900 font-medium">{sample.productName}</p>
                {sample.productSku && (
                  <p className="text-xs text-neutral-500">SKU: {sample.productSku}</p>
                )}
                <div className="mt-2 flex items-center gap-4">
                  <span className="text-sm text-neutral-600">
                    Quantity: <span className="font-medium">{sample.quantity} {sample.unit}</span>
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Tracking & Sample Details */}
          <Card variant="elevated">
            <div className="px-6 py-5 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                <BeakerIcon className="h-5 w-5 mr-2 text-neutral-400" />
                Tracking & Sample Details
              </h3>
            </div>
            <CardBody>
              <dl className="grid grid-cols-2 gap-4">
                {sample.trackingNumber && <div><dt className="text-sm font-medium text-neutral-500">Tracking Number</dt><dd className="mt-1 text-sm text-neutral-900 font-mono">{sample.trackingNumber}</dd></div>}
                {sample.lotNumber && <div><dt className="text-sm font-medium text-neutral-500">Lot Number</dt><dd className="mt-1 text-sm text-neutral-900 font-mono">{sample.lotNumber}</dd></div>}
                {sample.sampleCode && (
                  <div>
                    <dt className="text-sm font-medium text-neutral-500">Sample Code</dt>
                    <dd className="mt-1 text-sm text-neutral-900 font-mono">{sample.sampleCode}</dd>
                  </div>
                )}
                {sample.batchNumber && (
                  <div>
                    <dt className="text-sm font-medium text-neutral-500">Batch Number</dt>
                    <dd className="mt-1 text-sm text-neutral-900 font-mono">{sample.batchNumber}</dd>
                  </div>
                )}
              </dl>
            </CardBody>
          </Card>

          {/* Shipping Information */}
          {(sample.shippingCarrier || sample.shippingMethod || sample.shippingDate) && (
            <Card variant="elevated">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                  <TruckIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  Shipping Information
                </h3>
              </div>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4">
                  {sample.shippingCarrier && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Carrier</dt>
                      <dd className="mt-1 text-sm text-neutral-900">{sample.shippingCarrier}</dd>
                    </div>
                  )}
                  {sample.shippingMethod && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Method</dt>
                      <dd className="mt-1 text-sm text-neutral-900">{sample.shippingMethod}</dd>
                    </div>
                  )}
                  {sample.shippingDate && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Shipping Date</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {new Date(sample.shippingDate).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {sample.estimatedDeliveryDate && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Est. Delivery</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {new Date(sample.estimatedDeliveryDate).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {sample.actualDeliveryDate && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Actual Delivery</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {new Date(sample.actualDeliveryDate).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          )}

          {/* Client Feedback */}
          {sample.clientFeedback && (
            <Card variant="elevated">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  Client Feedback
                </h3>
              </div>
              <CardBody>
                <div className="space-y-3">
                  {sample.clientRating && (
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-neutral-500 mr-2">Rating:</span>
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`h-5 w-5 ${i < sample.clientRating! ? 'text-warning-400' : 'text-neutral-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-2 text-sm text-neutral-600">({sample.clientRating}/5)</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap">{sample.clientFeedback}</p>
                  </div>
                  {sample.feedbackDate && (
                    <p className="text-xs text-neutral-500">
                      Submitted on {new Date(sample.feedbackDate).toLocaleDateString()}
                    </p>
                  )}
                  {sample.clientDecision && (
                    <div className="mt-3 pt-3 border-t border-neutral-200">
                      <span className="text-sm font-medium text-neutral-500">Decision: </span>
                      <Badge variant={
                        sample.clientDecision === 'APPROVED' ? 'success' :
                        sample.clientDecision === 'REJECTED' ? 'error' :
                        'warning'
                      }>
                        {sample.clientDecision}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {!sample.clientFeedback && (
            <Card variant="elevated">
              <CardBody className="text-center py-8">
                <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900">No feedback yet</h3>
                <p className="mt-1 text-sm text-neutral-500">Client hasn't provided feedback for this sample.</p>
                <div className="mt-6">
                  <Button variant="primary" onClick={() => setShowFeedbackModal(true)}>
                    Add Feedback
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Notes */}
          {(sample.notes || sample.specialInstructions) && (
            <Card variant="elevated">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900">Additional Information</h3>
              </div>
              <CardBody className="space-y-4">
                {sample.notes && (
                  <div>
                    <p className="text-sm font-medium text-neutral-500">Notes</p>
                    <p className="mt-1 text-sm text-neutral-700 whitespace-pre-wrap">{sample.notes}</p>
                  </div>
                )}
                {sample.specialInstructions && (
                  <div>
                    <p className="text-sm font-medium text-neutral-500">Special Instructions</p>
                    <p className="mt-1 text-sm text-neutral-700 whitespace-pre-wrap">{sample.specialInstructions}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cost Information */}
          {(sample.sampleCost || sample.shippingCost || sample.totalCost) && (
            <Card variant="elevated">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  Cost Information
                </h3>
              </div>
              <CardBody className="space-y-3">
                {sample.sampleCost && (
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">Sample Cost</span>
                    <span className="text-sm font-medium text-neutral-900">
                      {sample.currency} {parseFloat(sample.sampleCost).toFixed(2)}
                    </span>
                  </div>
                )}
                {sample.shippingCost && (
                  <div className="flex justify-between">
                    <span className="text-sm text-neutral-500">Shipping Cost</span>
                    <span className="text-sm font-medium text-neutral-900">
                      {sample.currency} {parseFloat(sample.shippingCost).toFixed(2)}
                    </span>
                  </div>
                )}
                {sample.totalCost && (
                  <div className="flex justify-between pt-3 border-t border-neutral-200">
                    <span className="text-sm font-semibold text-neutral-900">Total Cost</span>
                    <span className="text-sm font-semibold text-neutral-900">
                      {sample.currency} {parseFloat(sample.totalCost).toFixed(2)}
                    </span>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Timeline */}
          <Card variant="elevated">
            <div className="px-6 py-5 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                <ClockIcon className="h-5 w-5 mr-2 text-neutral-400" />
                Timeline
              </h3>
            </div>
            <CardBody className="space-y-3">
              <div>
                <p className="text-xs font-medium text-neutral-500">Created</p>
                <p className="mt-1 text-sm text-neutral-900">
                  {new Date(sample.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500">Last Updated</p>
                <p className="mt-1 text-sm text-neutral-900">
                  {new Date(sample.updatedAt).toLocaleString()}
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Status Update Modal */}
      <StatusUpdateModal
        show={showStatusModal}
        sample={sample}
        onClose={() => setShowStatusModal(false)}
        onUpdate={fetchSample}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        show={showFeedbackModal}
        sample={sample}
        onClose={() => setShowFeedbackModal(false)}
        onUpdate={fetchSample}
      />

      {/* Quote Modal */}
      {showQuoteModal && sample?.client && (
        <QuoteModal
          deal={clientDeals[0]}
          client={sample.client}
          onClose={() => setShowQuoteModal(false)}
          onSuccess={() => {
            setShowQuoteModal(false);
          }}
          prefillData={{
            productId: sample?.productId,
            productName: sample?.productName,
            productSku: sample?.productSku,
            quantity: sample?.quantity ? parseFloat(sample.quantity) : undefined,
            unit: sample?.unit,
          }}
        />
      )}
    </div>
  );
};

export default SampleDetail;
